/**
 * Project Store - Manages Parking Lot projects
 * Persists to localStorage: map-editor-projects
 */

import { create } from 'zustand';
import { generateId, getCurrentTimestamp, storage } from '../lib/utils';
import type { Template } from '../../map-editor-entities/schema/types';
import { templateIdToNumber } from '../../map-editor-features/template/lib/templateIdMapping';
import type { TemplateId } from '../../map-editor-features/template';
import { log, LogCategory } from '../lib/logger';
import { useFloorStore } from './floorStore';

export interface ParkingLot {
  id: string;
  name: string;
  description?: string;
  /** CCTV IP 앞자리 (예: "172.19"). DXF 변환 시 카메라 IP 자동 구성에 사용. */
  cctvIpPrefix?: string;
  /** 현장에 실제로 쓰는 맵 (대시보드 표시용, 서버 map_projects.in_site_use). */
  inSiteUse?: boolean;
  thumbnail?: string; // Base64 encoded screenshot of the canvas
  created: string;
  modified: string;
  templateId?: number; // Numeric ID for backend API
  templateName?: string; // Template string ID ('parking', 'construction', 'factory')
  templateVersion?: string;
  availableObjectTypes?: Template['objectTypes'];
  relationTypes?: Template['relationTypes'];
  assets?: Template['assets'];
  initialObjects?: Template['initialObjects'];
}

interface ProjectState {
  lots: ParkingLot[];
  currentLot: string | null;

  /**
   * Create a parking lot.
   *
   * Optional `floorName` overrides the auto-generated first-floor name (B1).
   * Uniqueness rule: the (lot.name, floorName) combination must not already
   * exist across the workspace — different lots may share a floor name (e.g.
   * 'B1') as long as their lot names differ. This prevents users from saving
   * two indistinguishable rows in the dashboard list.
   */
  createLot: (
    lot: Omit<ParkingLot, 'id' | 'created' | 'modified'> & { floorName?: string }
  ) => ParkingLot;
  updateLot: (id: string, updates: Partial<ParkingLot>) => void;
  deleteLot: (id: string) => void;
  setCurrentLot: (id: string | null) => void;
  getLotById: (id: string) => ParkingLot | undefined;
  applyTemplate: (id: string, template: Template) => void;
  reloadFromStorage: () => void;
  setServerProject: (project: ParkingLot) => void;
}

const STORAGE_KEY = 'map-editor-projects';

// Load initial state from localStorage
const loadInitialState = (): Pick<ProjectState, 'lots' | 'currentLot'> => {
  const stored = storage.get<{ lots: ParkingLot[]; currentLot: string | null }>(
    STORAGE_KEY,
    { lots: [], currentLot: null }
  );
  return stored;
};

export const useProjectStore = create<ProjectState>((set, get) => {
  const initialState = loadInitialState();

  return {
    ...initialState,

    createLot: (lot) => {
      const state = get();
      const { floorName, ...lotFields } = lot;
      const trimmedFloorName = floorName?.trim() || '';

      // NOTE: (name, floorName) duplicate detection lives in DashboardPage —
      // it must run against the server's project list, not this store. `lots`
      // here is a localStorage cache that diverges from the dashboard's view
      // (stale entries from prior sessions, deletions that didn't sync, etc.),
      // so a check at this layer fires false positives like "P1 + B1 already
      // exists" against rows the user has no way to see or remove.

      const now = getCurrentTimestamp();
      const newLot: ParkingLot = {
        id: generateId(),
        ...lotFields,
        created: now,
        modified: now,
      };

      const newLots = [...state.lots, newLot];
      const newState = {
        lots: newLots,
        currentLot: newLot.id,
      };

      // Persist to localStorage
      storage.set(STORAGE_KEY, newState);

      set(newState);

      // CRITICAL: Auto-create the first floor when creating a new lot so
      // currentFloor is always set (DXF/CSV upload + editor flow require it).
      // Pass the user-supplied name through; addFloor falls back to B1 when empty.
      log.info(LogCategory.GENERAL, 'Auto-creating first floor for new lot', {
        lotId: newLot.id,
        lotName: newLot.name,
        floorName: trimmedFloorName || '(auto)',
      });
      useFloorStore.getState().addFloor(newLot.id, { name: trimmedFloorName || undefined });

      return newLot;
    },

    updateLot: (id, updates) => {
      const state = get();
      const lot = state.lots.find((l) => l.id === id);

      if (!lot) {
        throw new Error(`Parking lot with id "${id}" not found`);
      }

      // Validate name uniqueness if name is being updated
      if (updates.name) {
        const nameExists = state.lots.some(
          (l) => l.id !== id && l.name.toLowerCase() === updates.name!.toLowerCase()
        );

        if (nameExists) {
          throw new Error(`Parking lot with name "${updates.name}" already exists`);
        }
      }

      const updatedLot: ParkingLot = {
        ...lot,
        ...updates,
        modified: getCurrentTimestamp(),
      };

      const newLots = state.lots.map((l) => (l.id === id ? updatedLot : l));

      // currentLot이 이 lot을 가리키고 있고 id가 바뀌면 같이 갱신
      const newCurrentLot = state.currentLot === id && updates.id && updates.id !== id
        ? updates.id
        : state.currentLot;

      // Persist to localStorage
      storage.set(STORAGE_KEY, {
        lots: newLots,
        currentLot: newCurrentLot,
      });

      set({ lots: newLots, currentLot: newCurrentLot });

      // ID 변경 시 자식 store들의 외래 키(lotId) cascade 업데이트.
      // 백엔드 프로젝트 생성 후 localStorage 랜덤 ID → API 정수 ID 로 swap 하는
      // 흐름에서 floor 가 orphan 되는 문제를 막기 위함.
      if (updates.id && updates.id !== id) {
        try {
          useFloorStore.getState().reassignLotId(id, updates.id);
        } catch (err) {
          log.warn(LogCategory.STATE, 'updateLot: floor cascade failed', { from: id, to: updates.id, err });
        }
      }
    },

    deleteLot: (id) => {
      const state = get();
      const newLots = state.lots.filter((l) => l.id !== id);

      // If we're deleting the current lot, clear it
      const newCurrentLot = state.currentLot === id ? null : state.currentLot;

      const newState = {
        lots: newLots,
        currentLot: newCurrentLot,
      };

      // Persist to localStorage
      storage.set(STORAGE_KEY, newState);

      set(newState);

      // Clean up related data from other stores
      import('./floorStore').then(({ useFloorStore }) => {
        // Delete all floors for this lot
        const floors = useFloorStore.getState().floors.filter((f) => f.lotId === id);
        floors.forEach((floor) => {
          useFloorStore.getState().deleteFloor(floor.id);
        });
      });

      // Delete objectTypes and mappings for this lot
      import('./objectTypeStore').then(() => {
        const objectTypesKey = `map-editor-object-types-${id}`;
        storage.remove(objectTypesKey);
      });
    },

    setCurrentLot: (id) => {
      const state = get();

      // Validate that the lot exists
      if (id !== null && !state.lots.find((l) => l.id === id)) {
        throw new Error(`Parking lot with id "${id}" not found`);
      }

      // Persist to localStorage
      storage.set(STORAGE_KEY, {
        lots: state.lots,
        currentLot: id,
      });

      set({ currentLot: id });

      // CRITICAL: Auto-create first floor (B1) if lot has no floors
      // This handles both new lots and existing lots without floors
      if (id !== null) {
        const floorStore = useFloorStore.getState();
        const lotFloors = floorStore.floors.filter(f => f.lotId === id);

        if (lotFloors.length === 0) {
          log.info(LogCategory.GENERAL, 'Auto-creating first floor (B1) for lot without floors', {
            lotId: id,
            lotName: state.lots.find(l => l.id === id)?.name
          });
          floorStore.addFloor(id);
        }
      }
    },

    getLotById: (id) => {
      return get().lots.find((l) => l.id === id);
    },

    applyTemplate: (id, template) => {
      const state = get();
      const lot = state.lots.find((l) => l.id === id);

      if (!lot) {
        throw new Error(`Parking lot with id "${id}" not found`);
      }

      // Convert template string ID to numeric ID for backend
      const templateNumericId = templateIdToNumber(template.id as TemplateId);

      const updatedLot: ParkingLot = {
        ...lot,
        templateId: templateNumericId, // Numeric ID for backend
        templateName: template.id, // String ID for frontend
        templateVersion: template.version,
        availableObjectTypes: template.objectTypes,
        relationTypes: template.relationTypes,
        assets: template.assets,
        initialObjects: template.initialObjects,
        modified: getCurrentTimestamp(),
      };

      const newLots = state.lots.map((l) => (l.id === id ? updatedLot : l));
      const newState = { ...state, lots: newLots };

      // Persist to localStorage
      storage.set(STORAGE_KEY, {
        lots: newState.lots,
        currentLot: newState.currentLot,
      });

      set({ lots: newLots });
    },

    reloadFromStorage: () => {
      const stored = loadInitialState();
      log.info(LogCategory.STATE, 'Reloading from storage', {
        lotsCount: stored.lots.length,
        currentLot: stored.currentLot,
      });
      set({
        lots: stored.lots,
        currentLot: stored.currentLot,
      });
    },

    setServerProject: (project) => {
      const state = get();

      // Check if project already exists in store
      const existingIndex = state.lots.findIndex(l => l.id === project.id);

      let newLots;
      if (existingIndex >= 0) {
        // Update existing project
        newLots = [...state.lots];
        newLots[existingIndex] = project;
      } else {
        // Add new project
        newLots = [...state.lots, project];
      }

      // Set as current project
      const newState = {
        lots: newLots,
        currentLot: project.id,
      };

      // Persist to localStorage
      storage.set(STORAGE_KEY, newState);

      set(newState);

      log.info(LogCategory.STATE, 'Server project added to store', {
        projectId: project.id,
        projectName: project.name,
        templateId: project.templateId,
      });
    },
  };
});
